import { serviceMethodErrors } from './ServiceProvider.strings';
import Guid from './Guid';
import { Errors } from '../Helpers/Errors';
import urlHelper from '@Common/Helpers/UrlHelper';
import errorHelper from '@Common/Helpers/ErrorHelper';
import { properties } from '@Common/Core/Properties';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import { getRequestObject, parseJson, stringify } from '@Common/Helpers/JsonUtils';

export class ServiceProviderBase {
    private currentOption: IServiceMethodConfig;

    constructor(protected tenantId: string = null) {}

    public config(option: IServiceMethodConfig): this {
        this.currentOption = _.assign(this.currentOption ?? {}, option);
        return this;
    }

    protected executeServiceMethod<T>(param: IServiceMethod<T>): JQueryPromise<T> {
        const option = this.getOption(param);
        return $.Deferred<T>(def => {
            const ajaxSettings: JQueryAjaxSettings = {
                url: urlHelper.getDataServiceUrl() + '/' + param.methodName,
                type: param.httpMethod,
                contentType: 'application/json; charset=utf-8',
                dataType: 'text',
                data: this.prepareData(param, option.needPrepareRequest),
                headers: { CSRFToken: altosoft.csrfToken },
                success: (data: any) => {
                    def.resolve(parseJson(data));
                },
                error: (request: JQueryXHR, textStatus: string) => {
                    errorHelper.processError(option.exceptionText, request, option.dontShowErrorDialog);
                    def.reject(request, textStatus);
                },
                crossDomain: true,
                isLocal: properties.isLocalApp
            };

            if (param.cached) {
                delete ajaxSettings.data[param.sessionIdName];
                ajaxSettings.headers['Cache-Control'] = 'public, max-age=9999793';
            }
            $.ajax(ajaxSettings);
        }).promise();
    }

    private prepareData(param: IServiceMethod<any>, needPrepareRequest = true): string | Record<string, any> {
        if (_.has(param.data, 'tenantId') && _.isUndefined(param.data.tenantId)) {
            param.data.tenantId = this.tenantId;
        }
        const nonSetArgument = _.findKey(param.data, _.isUndefined);
        if (nonSetArgument) {
            Errors.invalidOperation(
                `Error calling ${param.methodName} method`,
                `'${nonSetArgument}' is undefined, and will not be passed to the function.`
            );
        }

        const data = needPrepareRequest ? getRequestObject(param.data) : param.data;

        if (param.tokenName) {
            data[param.tokenName] = { projectId: param.projectId };
        } else if (param.sessionIdName) {
            data[param.sessionIdName] = undefined;
        } else {
            commonUtils.log(`method ${param.methodName} without sessionId`);
        }

        if (param.httpMethod === 'POST') {
            return stringify(data);
        }
        return param.data;
    }

    private getOption(param: IServiceMethod<any>): IServiceMethodConfig {
        const option = _.defaults(
            this.currentOption,
            {
                exceptionText: param.exceptionText || serviceMethodErrors[param.methodName],
                dontShowErrorDialog: param.dontShowErrorDialog,
                needPrepareRequest: param.needPrepareRequest
            },
            { needPrepareRequest: true }
        );
        delete this.currentOption;
        return option;
    }
}

export interface IServiceMethodConfig {
    exceptionText?: string;
    needPrepareRequest?: boolean;
    dontShowErrorDialog?: boolean;
}

export interface IServiceMethod<T> extends IServiceMethodConfig, Object {
    methodName: string;
    data: _.Dictionary<any>;
    httpMethod?: 'POST' | 'GET';
    cached?: boolean;
    tokenName?: string;
    sessionIdName?: string;
    projectId?: Guid;
}
